import { useState } from 'react';
import { api } from '@/lib/api';
import { ScanLine, ShieldCheck, Loader2 } from 'lucide-react';
import { ProductCard } from '@/components/ProductCard';

const SAMPLE = '2C3CDZC91GH308566';

export default function VinLookup() {
    const [vin, setVin] = useState('');
    const [loading, setLoading] = useState(false);
    const [decoded, setDecoded] = useState(null);
    const [items, setItems] = useState([]);
    const [error, setError] = useState('');

    const run = async (e) => {
        e?.preventDefault();
        const v = vin.trim().toUpperCase();
        if (v.length < 11) {
            setError('VIN must be at least 11 characters.');
            return;
        }
        setError('');
        setLoading(true);
        try {
            const { data } = await api.post('/vin/parts', { vin: v });
            setDecoded(data.decoded);
            setItems(data.items || []);
        } catch (e) {
            setError(e?.response?.data?.detail || 'VIN decode failed.');
        } finally {
            setLoading(false);
        }
    };

    return (
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-10">
            <div className="max-w-3xl">
                <div className="inline-flex items-center gap-2 text-xs px-3 py-1 rounded-full bg-amber-500/10 border border-amber-500/30 text-amber-300 tracking-widest mb-3">
                    <ScanLine className="w-3.5 h-3.5" /> VIN LOOKUP
                </div>
                <h1 className="font-display text-4xl md:text-5xl text-white">DECODE YOUR VIN. SHOP THE EXACT PARTS.</h1>
                <p className="mt-3 text-white/60">We decode via NHTSA, then match parts that fit your year/make/model/trim. No more guesswork.</p>
            </div>

            <form onSubmit={run} className="mt-6 rrm-panel p-5 grid md:grid-cols-[1fr_auto] gap-3">
                <input
                    value={vin}
                    onChange={(e) => setVin(e.target.value.toUpperCase())}
                    placeholder={`e.g. ${SAMPLE}`}
                    maxLength={17}
                    data-testid="vin-lookup-input"
                    className="font-mono tracking-[0.2em] w-full bg-white/5 border border-white/10 rounded-md px-4 py-3 text-lg focus:outline-none focus:ring-2 focus:ring-amber-400/40"
                />
                <button
                    disabled={loading}
                    data-testid="vin-lookup-submit-button"
                    className="px-5 py-3 rounded-md bg-[var(--rrm-red)] hover:bg-[var(--rrm-red2)] text-white font-semibold inline-flex items-center gap-2 disabled:opacity-50"
                >
                    {loading ? <Loader2 className="w-4 h-4 animate-spin" /> : <ScanLine className="w-4 h-4" />}
                    Decode VIN
                </button>
            </form>
            {error && <div className="mt-3 text-rose-300 text-sm">{error}</div>}

            {decoded && (
                <section className="mt-8" data-testid="vin-decoded-vehicle-card">
                    <div className="rrm-panel p-5">
                        <div className="grid sm:grid-cols-2 lg:grid-cols-5 gap-4">
                            <Stat label="VIN" value={decoded.vin} mono />
                            <Stat label="Year" value={decoded.year} />
                            <Stat label="Make" value={decoded.make} />
                            <Stat label="Model" value={decoded.model} />
                            <Stat label="Trim" value={decoded.trim} />
                            <Stat label="Engine" value={decoded.engine} />
                            <Stat label="Body" value={decoded.body} />
                            <Stat label="Drive" value={decoded.drive_type} />
                            <Stat label="Fuel" value={decoded.fuel} />
                            <Stat label="Built" value={decoded.plant_country} />
                        </div>
                    </div>
                </section>
            )}

            {items.length > 0 && (
                <section className="mt-8" data-testid="vin-matching-parts-grid">
                    <div className="flex items-end justify-between mb-4">
                        <h2 className="font-display text-2xl text-white inline-flex items-center gap-2">
                            <ShieldCheck className="w-5 h-5 text-emerald-400" /> GUARANTEED FIT PARTS ({items.length})
                        </h2>
                    </div>
                    <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-4">
                        {items.map((p) => <ProductCard key={p.id} product={p} />)}
                    </div>
                </section>
            )}
            {decoded && items.length === 0 && (
                <div className="mt-8 rrm-panel p-6 text-white/70">
                    Vehicle decoded, but no matching parts in catalog for this VIN yet. Browse the full <a href="/shop" className="text-amber-300">shop</a> or ask the AI mechanic.
                </div>
            )}
        </div>
    );
}

function Stat({ label, value, mono }) {
    return (
        <div>
            <div className="text-[10px] text-white/50 tracking-widest uppercase">{label}</div>
            <div className={`${mono ? 'font-mono' : 'font-display text-xl'} text-white mt-0.5`}>{value || '—'}</div>
        </div>
    );
}
